import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Alert,
    CircularProgress,
    Autocomplete
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import modalStyle from "../../styles/modalStyle";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import apiClient from "../../axiosClient";
import { decodedJWT, getOwnerName } from "../../utils/commonUtils";
import { findFieldAlias, findColumnIndex, extractUniqueValuesFromColumn } from "../../utils/excelUtils";
import { processExcelDataWithValidation } from "../../utils/uploadValidationUtils";
import { FieldAlias } from "../../interfaces/field";
import FieldAliasService from "../../services/fieldAliasService";
import { v4 as uuidv4 } from 'uuid';

interface MappingEntry {
    id: string;
    clientValue: string;
    standardValue: string;
}

interface FieldValueMappingModalProps {
    open: boolean;
    onClose: () => void;
    loanType: string; // Current loan type being configured
}

const FieldValueMappingModal: React.FC<FieldValueMappingModalProps> = ({
    open,
    onClose,
    loanType
}) => {
    const [dpdBucketMappings, setDpdBucketMappings] = useState<MappingEntry[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldAliases, setFieldAliases] = useState<FieldAlias[]>([]);
    const [loadingFieldAliases, setLoadingFieldAliases] = useState(false);
    
    const picklists = useSelector((state: RootState) => state.picklists.picklists);
    const token = decodedJWT();
    const clientName = getOwnerName(token);

    const standardDpdBuckets = picklists.DPD_BUCKET || [];

    // Check if user has field alias for dpd_bucket
    const hasDpdBucketAlias = fieldAliases.some(alias => 
        alias.field_name === 'dpd_bucket' 
    );
    const canShowUpload = hasDpdBucketAlias;

    // Helper functions for validation
    const checkForDuplicates = (mappings: MappingEntry[]): string[] => {
        const valueCount: Record<string, number> = {};
        const duplicates: string[] = [];
        
        mappings.forEach(mapping => {
            const trimmedValue = mapping.clientValue.trim();
            if (trimmedValue) {
                valueCount[trimmedValue] = (valueCount[trimmedValue] || 0) + 1;
                if (valueCount[trimmedValue] > 1 && !duplicates.includes(trimmedValue)) {
                    duplicates.push(trimmedValue);
                }
            }
        });
        
        return duplicates;
    };

    const hasDuplicateValues = (mappings: MappingEntry[]): boolean => {
        return checkForDuplicates(mappings).length > 0;
    };

    const isDuplicateValue = (value: string, mappings: MappingEntry[], currentId: string): boolean => {
        const trimmedValue = value.trim();
        return mappings.some(mapping => 
            mapping.id !== currentId && 
            mapping.clientValue.trim() === trimmedValue
        );
    };

    // Convert Record to MappingEntry array
    const recordToMappingEntries = (record: Record<string, string>): MappingEntry[] => {
        return Object.entries(record).map(([clientValue, standardValue]) => ({
            id: uuidv4(),
            clientValue,
            standardValue
        }));
    };

    // Convert MappingEntry array to Record
    const mappingEntriesToRecord = (entries: MappingEntry[]): Record<string, string> => {
        const record: Record<string, string> = {};
        entries.forEach(entry => {
            if (entry.clientValue.trim()) {
                record[entry.clientValue] = entry.standardValue;
            }
        });
        return record;
    };

    useEffect(() => {
        if (open && loanType) {
            loadFieldAliases();
            loadExistingMappings();
        }
    }, [open, loanType]);

    const loadFieldAliases = async () => {
        if (!loanType) return;
        
        setLoadingFieldAliases(true);
        try {
            const fieldAliases = await FieldAliasService.getFieldAliases(loanType);
            setFieldAliases(fieldAliases);
        } catch (error) {
            console.error('Error loading field aliases:', error);
        } finally {
            setLoadingFieldAliases(false);
        }
    };

    const loadExistingMappings = async () => {
        if (!loanType) return;
        
        setLoading(true);
        try {
            const response = await apiClient.get("/api/v1/field_value_map", {
                params: { loan_type: loanType }
              });
            const mappings = response.data;
            
            // Find existing mappings for DPD_BUCKET only
            const dpdBucketMapping = mappings.find((m: any) => m.picklist_type === 'DPD_BUCKET');
            
            if (dpdBucketMapping) {
                setDpdBucketMappings(recordToMappingEntries(dpdBucketMapping.map || {}));
            }
        } catch (error) {
            console.error('Error loading mappings:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateMappingById = (id: string, field: 'clientValue' | 'standardValue', value: string) => {
        setDpdBucketMappings(prev => prev.map(mapping => 
            mapping.id === id ? { ...mapping, [field]: value } : mapping
        ));
    };

    const addNewMapping = () => {
        const newMapping: MappingEntry = {
            id: uuidv4(),
            clientValue: '',
            standardValue: ''
        };
        
        setDpdBucketMappings(prev => [...prev, newMapping]);
    };

    const removeMapping = (id: string) => {
        setDpdBucketMappings(prev => prev.filter(mapping => mapping.id !== id));
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            // Process Excel file with proper validation (requires Sheet1)
            const validation = await processExcelDataWithValidation(file);
            
            if (!validation.isValid) {
                alert(validation.errors.join('\n'));
                return;
            }

            // Get headers from validated data and clean them
            const rawHeaders = validation.data?.[0] || [];
            const headers = rawHeaders
                .map(header => header != null ? String(header).trim() : '')
                .filter(header => header !== '');
            const jsonData = validation.data || [];
            
            // Find field alias for DPD bucket only
            const dpdBucketAlias = findFieldAlias(fieldAliases, 'dpd_bucket');

            // Find column index for DPD bucket
            const dpdBucketColumnIndex = dpdBucketAlias 
                ? findColumnIndex(headers, dpdBucketAlias.alias_name || '') 
                : -1;

            // Extract unique DPD bucket values
            const uniqueDpdBuckets = dpdBucketColumnIndex >= 0 
                ? extractUniqueValuesFromColumn(jsonData, dpdBucketColumnIndex) 
                : new Set<string>();

            // Update mappings with extracted values
            let updatedCount = 0;

            if (uniqueDpdBuckets.size > 0) {
                setDpdBucketMappings(prev => {
                    const existingValues = new Set(prev.map(m => m.clientValue.trim()));
                    const newMappings = [...prev];
                    
                    uniqueDpdBuckets.forEach(value => {
                        if (!existingValues.has(value.trim())) {
                            newMappings.push({
                                id: uuidv4(),
                                clientValue: value,
                                standardValue: ''
                            });
                            updatedCount++;
                        }
                    });
                    
                    return newMappings;
                });
            }

            // Provide feedback to user
            if (updatedCount > 0) {
                alert(`Added ${updatedCount} new DPD bucket values to mapping: ${Array.from(uniqueDpdBuckets).join(', ')}`);
            } else if (dpdBucketColumnIndex === -1) {
                alert('No dpd_bucket column found in the file. Please ensure you have created a field alias for dpd_bucket and it matches a column header in your Excel file.');
            } else {
                alert('No new unique values found to add to mappings.');
            }

        } catch (error: any) {
            console.error('Error processing Excel file:', error);
            alert(error.message || 'Error processing Excel file. Please ensure it\'s a valid Excel file with a worksheet named "Sheet1".');
        }
        
        // Clear the input value to allow uploading the same file again
        event.target.value = '';
    };

    const saveMappings = async () => {
        setSaving(true);
        setError(null);
        
        try {
            // Check for duplicates before saving
            const dpdBucketDuplicates = checkForDuplicates(dpdBucketMappings);
            
            if (dpdBucketDuplicates.length > 0) {
                setError(`Cannot save mappings with duplicate values: ${dpdBucketDuplicates.join(', ')}`);
                return;
            }
            
            const mappingsToSave = [];
            
            // Convert array back to Record format for API
            if (dpdBucketMappings.length > 0) {
                const dpdBucketRecord = mappingEntriesToRecord(dpdBucketMappings);
                if (Object.keys(dpdBucketRecord).length > 0) {
                    mappingsToSave.push({
                        client_name: clientName || '',
                        loan_type: loanType,
                        picklist_type: 'DPD_BUCKET',
                        map: dpdBucketRecord
                    });
                }
            }

            if (mappingsToSave.length > 0) {
                await apiClient.put('/api/v1/field_value_map', mappingsToSave);
            }
            
            onClose();
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to save mappings');
        } finally {
            setSaving(false);
        }
    };

    const renderMappingTable = (mappings: MappingEntry[], standardValues: string[]) => {
        return (
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Your File Value</TableCell>
                            <TableCell>Standard DPD Bucket Value</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mappings.map((mapping) => {
                            const isDuplicate = isDuplicateValue(mapping.clientValue, mappings, mapping.id);
                            const hasEmptyValue = !mapping.clientValue.trim();
                            
                            return (
                                <TableRow key={mapping.id}>
                                    <TableCell>
                                        <TextField
                                            value={mapping.clientValue}
                                            onChange={(e) => {
                                                updateMappingById(mapping.id, 'clientValue', e.target.value);
                                            }}
                                            size="small"
                                            fullWidth
                                            error={isDuplicate || hasEmptyValue}
                                            helperText={
                                                hasEmptyValue ? 'Value cannot be empty' :
                                                isDuplicate ? 'Duplicate value detected' : ''
                                            }
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&.Mui-error': {
                                                        '& fieldset': {
                                                            borderColor: '#f44336',
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            size="small"
                                            options={standardValues}
                                            value={mapping.standardValue}
                                            onChange={(_, newValue) => {
                                                updateMappingById(mapping.id, 'standardValue', newValue || '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    placeholder="Select standard value"
                                                    size="small"
                                                />
                                            )}
                                            sx={{ minWidth: 200 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => removeMapping(mapping.id)}
                                        >
                                            Remove
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <Box p={2}>
                    <Button
                        variant="outlined"
                        onClick={() => addNewMapping()}
                        size="small"
                    >
                        Add New DPD Bucket Mapping
                    </Button>
                </Box>
            </TableContainer>
        );
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Configure DPD Bucket Value Mappings - {loanType}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Map your custom DPD bucket values to standard picklist values. This will be used during file uploads to automatically convert your values.
                </Alert>

                {canShowUpload ? (
                    <Box display="flex" justifyContent="center" mb={2}>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            component="label"
                            color="secondary"
                        >
                            Upload Excel File (Sheet1) to Extract Values
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                hidden
                                onChange={handleFileUpload}
                            />
                        </Button>
                    </Box>
                ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        To upload an Excel file and extract values, you must first create a field alias for 'dpd_bucket' in the Configure Field Aliases section.
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {(loading || loadingFieldAliases) ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="subtitle1" mb={2}>
                            DPD Bucket Value Mappings
                        </Typography>
                        {renderMappingTable(dpdBucketMappings, standardDpdBuckets)}
                    </Box>
                )}

                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={saveMappings}
                        disabled={
                            saving || 
                            loading || 
                            loadingFieldAliases ||
                            hasDuplicateValues(dpdBucketMappings) ||
                            dpdBucketMappings.some(m => !m.clientValue.trim())
                        }
                    >
                        {saving ? <CircularProgress size={20} /> : 'Save Mappings'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default FieldValueMappingModal;